import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import ProseContent from '../components/ProseContent'

const subprocessors = [
  { name: 'Amazon Web Services', purpose: 'Cloud hosting and storage', location: 'US / EU' },
  { name: 'Stripe', purpose: 'Payments and Connect payouts', location: 'US / EU' },
  { name: 'Cloudflare', purpose: 'Content delivery and DDoS protection', location: 'Global' },
  { name: 'Postmark', purpose: 'Transactional email', location: 'US' },
  { name: 'Datadog', purpose: 'Logging, monitoring, incident response', location: 'US / EU' },
  { name: 'Segment', purpose: 'Product analytics routing', location: 'US' },
  { name: 'Linear', purpose: 'Engineering issue tracking', location: 'US' },
]

const sections = [
  {
    heading: 'Our role under the GDPR',
    body: (
      <>
        <p>
          Oak Platforms Inc. acts as a <strong>data processor</strong> when we handle personal data on behalf of a moderator running an Oak-powered marketplace. For our own website, sales, and platform operations, Oak is a <strong>data controller</strong>.
        </p>
        <p>
          Our Data Processing Addendum (DPA), available on request at <a href="mailto:privacy@oakplatforms.com" className="text-oak-black underline underline-offset-2">privacy@oakplatforms.com</a>, incorporates the 2021 EU Standard Contractual Clauses and the UK International Data Transfer Addendum by default.
        </p>
      </>
    ),
  },
  {
    heading: 'Your rights as a data subject',
    body: (
      <>
        <p>
          If you live in the European Economic Area, the United Kingdom, or Switzerland, the GDPR and equivalent laws give you the right to:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Access the personal data we hold about you.</li>
          <li>Request correction of inaccurate or incomplete data.</li>
          <li>Request deletion of data when there is no overriding legal basis to keep it.</li>
          <li>Restrict or object to specific kinds of processing.</li>
          <li>Receive your data in a portable, machine-readable format.</li>
          <li>Withdraw consent you have previously given, without affecting prior processing.</li>
          <li>Lodge a complaint with your local supervisory authority.</li>
        </ul>
        <p>
          For data held inside a specific Oak-powered marketplace, send your request to the moderator who runs that marketplace. Oak will assist them in responding within the statutory timelines.
        </p>
      </>
    ),
  },
  {
    heading: 'Legal bases for processing',
    body: (
      <>
        <p>Oak processes personal data on the following legal bases:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>
            <strong>Contract</strong> — to provide the platform to our customers.
          </li>
          <li>
            <strong>Legitimate interests</strong> — to operate, secure, and improve Oak, balanced against the rights of data subjects.
          </li>
          <li>
            <strong>Consent</strong> — for optional marketing communications and non-essential cookies, which you can withdraw at any time.
          </li>
          <li>
            <strong>Legal obligation</strong> — where we are required to retain data to meet financial, tax, or regulatory requirements.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: 'International data transfers',
    body: (
      <>
        <p>
          Oak is a US company. Personal data from the EEA, UK, or Switzerland may be transferred to the United States or to other countries where our subprocessors operate. For those transfers we rely on the European Commission's 2021 Standard Contractual Clauses, the UK Addendum, and — where applicable — adequacy decisions.
        </p>
        <p>
          We complement contractual safeguards with technical measures: encryption in transit (TLS 1.2+) and at rest (AES-256), strict access controls, and independent audits.
        </p>
      </>
    ),
  },
  {
    heading: 'Subprocessors',
    body: (
      <>
        <p>
          The following subprocessors currently support the Oak platform. We notify customers at least 30 days before adding a new subprocessor that processes personal data.
        </p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-oak-black/10">
          <table className="w-full border-collapse text-left text-[14px] tracking-[-0.1px]">
            <thead className="bg-oak-cream">
              <tr>
                <th className="px-4 py-3 font-semibold text-oak-black">Provider</th>
                <th className="px-4 py-3 font-semibold text-oak-black">Purpose</th>
                <th className="px-4 py-3 font-semibold text-oak-black">Region</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-oak-black/10">
              {subprocessors.map((s) => (
                <tr key={s.name}>
                  <td className="px-4 py-3 font-medium text-oak-black">{s.name}</td>
                  <td className="px-4 py-3 text-oak-black/70">{s.purpose}</td>
                  <td className="px-4 py-3 text-oak-black/70">{s.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    heading: 'Security measures',
    body: (
      <>
        <p>Oak operates a documented security program that includes:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256).</li>
          <li>Role-based access controls and single sign-on for employees, with mandatory MFA.</li>
          <li>Secure software development lifecycle with mandatory code review and automated scanning.</li>
          <li>Continuous monitoring, logging, and anomaly detection.</li>
          <li>Annual penetration testing by an independent third party.</li>
          <li>A documented incident-response plan with clear notification timelines.</li>
        </ul>
      </>
    ),
  },
  {
    heading: 'Data retention and deletion',
    body: (
      <>
        <p>
          Oak retains personal data only for as long as necessary for the purposes described in our Privacy Policy and this page. On termination of a customer contract, we return or delete customer personal data within 30 days, except where retention is required by law (for example, financial record-keeping).
        </p>
      </>
    ),
  },
  {
    heading: 'Breach notification',
    body: (
      <>
        <p>
          If Oak becomes aware of a personal-data breach that affects customer data, we notify the affected customers without undue delay — in any event within 72 hours of confirmation — with the information they need to meet their own GDPR obligations.
        </p>
      </>
    ),
  },
  {
    heading: 'Data Protection Officer and EU representative',
    body: (
      <>
        <p>
          Oak has appointed an internal privacy lead who acts as the point of contact for GDPR matters. You can reach them at <a href="mailto:privacy@oakplatforms.com" className="text-oak-black underline underline-offset-2">privacy@oakplatforms.com</a>.
        </p>
        <p>
          Our appointed EU representative under Article 27 GDPR can be contacted on request at the same address.
        </p>
      </>
    ),
  },
  {
    heading: 'Submitting a request',
    body: (
      <>
        <p>
          To exercise your rights under the GDPR, send a request to <a href="mailto:privacy@oakplatforms.com" className="text-oak-black underline underline-offset-2">privacy@oakplatforms.com</a>. We will confirm receipt within five business days and respond substantively within the statutory one-month window.
        </p>
      </>
    ),
  },
]

export default function GDPRPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="GDPR Compliance"
        title="How Oak handles personal data under EU and UK law."
        description="Our commitments as a processor and a controller, our safeguards for international transfers, and how to exercise your rights."
      />
      <ProseContent
        updated="April 1, 2026"
        intro={
          <p>
            This page complements our Privacy Policy and is aimed at users and customers in the European Economic Area, the United Kingdom, and Switzerland.
          </p>
        }
        sections={sections}
      />
    </PageLayout>
  )
}
